import { formatCurrency, formatDate, formatDateTime } from '@eriko/shared-types';

interface Ref {
  name?: string;
  value?: string;
}

interface AccountBasedExpenseLineDetail {
  AccountRef?: Ref;
  BillableStatus?: string;
}

interface ItemBasedExpenseLineDetail {
  ItemRef?: Ref;
  BillableStatus?: string;
}

interface BillLine {
  Id?: string;
  Amount?: number;
  Description?: string;
  DetailType?: string;
  AccountBasedExpenseLineDetail?: AccountBasedExpenseLineDetail;
  ItemBasedExpenseLineDetail?: ItemBasedExpenseLineDetail;
}

export function BillReceiptReport({ data }: { data: Record<string, unknown> }) {
  const entityType = String(data.entityType ?? 'Bill');
  const title = entityType === 'Purchase' ? 'Purchase' : 'Bill';

  const vendor = (data.VendorRef as Ref | undefined)?.name ?? '';
  const txnDate = data.TxnDate ? String(data.TxnDate) : '';
  const paymentType = data.PaymentType ? String(data.PaymentType) : '';
  const currencyName =
    (data.CurrencyRef as Ref | undefined)?.name ?? 'United States Dollar';
  const totalAmt = Number(data.TotalAmt ?? 0);
  const lastUpdated =
    (data.MetaData as { LastUpdatedTime?: string } | undefined)?.LastUpdatedTime ?? '';

  const lines = ((data.Line as BillLine[] | undefined) ?? []).filter(
    (l) =>
      l.DetailType === 'AccountBasedExpenseLineDetail' ||
      l.DetailType === 'ItemBasedExpenseLineDetail',
  );

  const metaRows: Array<[string, string]> = [
    ['Date', txnDate ? formatDate(txnDate) : '—'],
    ['Payment', paymentType || '—'],
    ['Currency', currencyName],
  ];

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 px-8 py-7">
      {/* Top: Vendor | Title + meta */}
      <div className="flex items-start justify-between gap-8">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400 mb-2">
            Vendor
          </div>
          <div className="text-sm text-surface-900">{vendor || '—'}</div>
        </div>

        <div className="text-right shrink-0">
          <div
            className="text-[28px] leading-none text-surface-900 mb-3"
            style={{ fontFamily: '"Instrument Serif", Georgia, serif', fontWeight: 400 }}
          >
            {title}
          </div>
          <div className="space-y-0.5">
            {metaRows.map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-end gap-3 text-[13px]">
                <span className="text-surface-500">{label}</span>
                <span className="text-surface-900 font-medium min-w-[8rem] text-right">
                  {value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="my-7 border-t border-surface-200" />

      {/* Line items table */}
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400 pb-3">
              Account
            </th>
            <th className="text-center text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400 pb-3 w-[120px]">
              Billable
            </th>
            <th className="text-right text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400 pb-3 w-[140px]">
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, i) => {
            const accountName =
              line.AccountBasedExpenseLineDetail?.AccountRef?.name ??
              line.ItemBasedExpenseLineDetail?.ItemRef?.name ??
              '—';
            const billableStatus =
              line.AccountBasedExpenseLineDetail?.BillableStatus ??
              line.ItemBasedExpenseLineDetail?.BillableStatus;
            const isBillable = billableStatus === 'Billable';
            const amount = Number(line.Amount ?? 0);
            const description = line.Description ? String(line.Description) : '';

            return (
              <tr key={String(line.Id ?? i)} className="border-t border-surface-100">
                <td className="py-4 align-top">
                  <div className="text-sm text-surface-900">{accountName}</div>
                  {description && (
                    <div className="text-xs text-surface-500 mt-0.5">{description}</div>
                  )}
                </td>
                <td className="py-4 text-center align-top">
                  <span className="inline-flex items-center justify-center rounded-full bg-surface-100 px-2.5 py-0.5 text-xs text-surface-600">
                    {isBillable ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="py-4 text-right align-top">
                  <span className="font-mono text-sm text-surface-900">
                    {formatCurrency(amount, 2)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Total */}
      <div className="flex justify-end mt-3">
        <div className="w-65 border-t-2 border-surface-900 pt-3 flex items-center justify-between">
          <span className="text-sm text-surface-700">Total</span>
          <span className="font-mono text-base font-semibold text-surface-900">
            {formatCurrency(totalAmt, 2)}
          </span>
        </div>
      </div>

      {/* Divider */}
      <div className="mt-7 mb-5 border-t border-surface-200" />

      {/* Last updated */}
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400 mb-1.5">
          Last Updated
        </div>
        <div className="text-sm text-surface-900">
          {lastUpdated ? formatDateTime(lastUpdated) : '—'}
        </div>
      </div>
    </div>
  );
}
